import { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import type { MarbleGame } from 'src/types/domain/Game'
import { getLatestEvent } from 'src/utils/raceUtils'
import {
  phaseRank,
  rememberAnnouncedPhase,
  shouldAnnouncePhase
} from './racePhaseAnnouncement'
import { useBetActions } from 'src/store/bet'
import { useGetGameConfig } from 'src/store/game'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import { useSoundNotification } from 'src/hooks/useSoundNotification'
import { useSoundEffect } from 'src/hooks/useSoundEffect'
import { isBirdieGameType, isCrashGameType } from 'src/utils/gameTypeUtils'
import { getSettledTimeWindow } from 'src/utils/getSettledTimeWindow'
import { ShowRaceBanner } from './ShowRaceBanner'

type Props = Readonly<{
  game: MarbleGame
}>

export function ShowRaceBannerContainer({ game }: Props) {
  const betActions = useBetActions()
  const { playNotification } = useSoundNotification()
  const { playEffect } = useSoundEffect()

  // In chip-placement mode the lightning round must stay hidden during
  // betting (Crazy Time / Evolution-style flow). The visual banner is
  // already suppressed below via the early return, but we ALSO have
  // to gate the lightning sound effect that fires on race_ready —
  // otherwise the audio cue tells the player "this is a lightning
  // round" before they've placed bets, defeating the gate.
  const gameConfig = useGetGameConfig()
  const isChipPlacement =
    isKnownGameConfig(gameConfig) && gameConfig.bettingMode === 'chip-placement'

  const SETTLED_COUNTDOWN_TIME = useMemo(() => getSettledTimeWindow(game), [game])

  const [enableBanner, setEnableBanner] = useState<boolean>(false)
  const [showBanner, setShowBanner] = useState(false)
  const [exitBanner, setExitBanner] = useState(false)

  const activeTimeoutRef = useRef<NodeJS.Timeout>()
  const hideTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const componentMountTimeRef = useRef(Date.now())
  // raceId -> highest lifecycle phase already announced for it. A ref, not
  // state: setState is async, so two updates landing in one commit would both
  // read the stale value and announce twice.
  const announcedPhasesRef = useRef<Map<string, number>>(new Map())
  const currentRace = game.races[0]
  const currentBet = betActions.getBetByRaceId(currentRace.uuid)
  // For multi-bet rounds (chip-placement, side-bets on multiple
  // markets), `currentBet` only surfaces the first bet found — which
  // means the settle sound below picks "won" or "lost" essentially
  // at random based on insertion order. Check ALL bets for the round
  // so the audio reflects the player's actual outcome: any-won →
  // "won" sound, otherwise any-lost → "lost" sound.
  const raceBets = betActions.getBetsByRaceId(currentRace.uuid)
  const hasWonBet = raceBets.some((b) => b.status === 'won' || b.status === 'cashed_out')
  const hasLostBet = raceBets.some((b) => b.status === 'lost')

  const clearActiveTimeout = useCallback(() => {
    if (activeTimeoutRef.current) {
      clearTimeout(activeTimeoutRef.current)
      activeTimeoutRef.current = undefined
    }
  }, [])

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = undefined
    }
  }, [])

  const showRaceSettledBanner = useCallback(
    (skipTransition = false) => {
      clearActiveTimeout()
      clearHideTimeout()
      setEnableBanner(true)

      if (skipTransition) {
        setExitBanner(false)
        setShowBanner(true)
      } else {
        setShowBanner(false)
        setExitBanner(true)

        activeTimeoutRef.current = setTimeout(() => {
          setExitBanner(false)
          setShowBanner(true)
        }, 50)
      }

      // Set timeout to hide banner when the settled countdown ends
      hideTimeoutRef.current = setTimeout(() => {
        setShowBanner(false)
        setExitBanner(true)
      }, SETTLED_COUNTDOWN_TIME * 1000)
    },
    [SETTLED_COUNTDOWN_TIME, clearActiveTimeout, clearHideTimeout]
  )

  useEffect(() => {
    const latestEvent = getLatestEvent(currentRace.raceEvents)
    // Announce each phase at most once PER ROUND, forward-only. The old guard
    // compared the bare event type against the last one shown, with no round
    // identity and no ordering, so anything that moved the round backwards read
    // as a fresh transition and replayed its voice-over. Kept in a ref rather
    // than state: setState is async, so two updates in one commit would both
    // read the stale value and both announce.
    if (latestEvent && shouldAnnouncePhase(
      currentRace.uuid, latestEvent.eventType, announcedPhasesRef.current
    )) {
      // Read before recording: a settle that arrives while the round had only
      // reached ended/crashed is the "quick settlement" case (both share a
      // rank, so one comparison covers either game family).
      const previousRank = announcedPhasesRef.current.get(currentRace.uuid)
      rememberAnnouncedPhase(currentRace.uuid, latestEvent.eventType, announcedPhasesRef.current)

      const isQuickSettlement =
        latestEvent.eventType === 'race_settled' && previousRank === phaseRank('race_ended')

      switch (latestEvent.eventType) {
        case 'race_scheduled':
          setEnableBanner(false)
          playNotification('notification')
          playNotification('notificationRaceScheduled')
          break

        case 'race_ready':
          setEnableBanner(false)
          playNotification('notification')
          playNotification('notificationRaceReady')
          // Classic mode keeps the lightning announce-sound at
          // race_ready (current behavior). Chip-placement defers the
          // sound — `ChipPlacementPanel` plays it at the reveal moment
          // when betting closes, alongside the strike animation.
          if (currentRace.isLightningRound && !isChipPlacement) {
            playEffect('lightningRound')
          }
          break

        case 'race_starting':
          setEnableBanner(false)
          if (
            game.track.gameType !== 'coin_flip' &&
            game.track.gameType !== 'fishtank' &&
            game.track.gameType !== 'irl' &&
            game.track.gameType !== 'irl_alt' &&
            game.track.gameType !== 'duck_river' &&
            game.track.gameType !== 'snow_run' &&
            game.track.gameType !== 'social_betting' &&
            game.track.gameType !== 'crash'
          ) {
            playNotification('notification')
            playNotification('notificationRaceStarting')
          }
          break

        case 'race_started': {
          setEnableBanner(false)
          playNotification('notification')

          const raceStartTime = new Date(latestEvent.eventTimestamp).getTime()
          const timeSinceRaceStart = componentMountTimeRef.current - raceStartTime

          if (timeSinceRaceStart > 1000) {
            playNotification('notificationRaceStartedAlt')
          } else {
            playNotification('notificationRaceStarted')
          }

          // fadeOutMusic(2000)
          break
        }

        case 'race_crashed':
        case 'race_ended':
          setEnableBanner(false)
          if (
            game.track.gameType !== 'coin_flip' &&
            game.track.gameType !== 'fishtank' &&
            game.track.gameType !== 'irl' &&
            game.track.gameType !== 'irl_alt' &&
            game.track.gameType !== 'duck_river' &&
            game.track.gameType !== 'snow_run' &&
            game.track.gameType !== 'social_betting' &&
            game.track.gameType !== 'crash' &&
            // Birdie's clip runs on past `race_ended` — the third putt settles and the footage keeps
            // playing — so "the round has ended" lands over a round the viewer can still see going.
            // The other cues are fine and stay; only this one contradicts the screen.
            !isBirdieGameType(game.track.gameType)
          ) {
            playNotification('notification')
            playNotification('notificationRaceEnded')
          }
          // fadeInMusic(2000)
          break

        case 'race_settled':
          showRaceSettledBanner(isQuickSettlement)

          // Crash has its own in-clip audio + in-panel win/busted cues, so
          // the widget's round-settled / won / lost notification chimes are
          // suppressed for it. The pre-round (race_ready "place your bets")
          // and race_started phase cues are left ON above — the player still
          // hears those. Birdie is suppressed here too: its board holds the
          // result until the viewer's picture has caught up and plays its own
          // gated win chime then (BirdieBoard `totalWin` effect); this one fires
          // on the socket callback, ahead of that hold. Every other game keeps
          // the full settle/won/lost audio.
          if (!isCrashGameType(game.track.gameType) && !isBirdieGameType(game.track.gameType)) {
            playNotification('notification')

            if (hasWonBet) {
              playNotification('notificationRaceSettledWon1')
            } else if (hasLostBet) {
              // Randomly choose between lost1 and lost2 sounds
              const lostSound =
                Math.random() < 0.5
                  ? 'notificationRaceSettledLost1'
                  : 'notificationRaceSettledLost2'
              playNotification(lostSound)
            } else {
              playNotification('notificationRaceSettled')
            }
          }

          // fadeInMusic(2000)
          break
      }
    }
  }, [
    currentRace,
    hasWonBet,
    hasLostBet,
    showRaceSettledBanner,
    playNotification,
    playEffect,
    isChipPlacement
  ])

  useEffect(() => {
    return () => {
      clearActiveTimeout()
    }
  }, [clearActiveTimeout])

  const latestEventType = getLatestEvent(currentRace.raceEvents)?.eventType

  // Banner suppression: in chip-placement mode the per-option WINNER
  // tag + payout-pill morph on `ChipPlacementPanel` already communicates
  // the outcome (and supports multi-bet rounds, which the single-
  // `currentBet` banner never could).
  if (!enableBanner || isChipPlacement) return null

  return (
    <ShowRaceBanner
      currentBet={currentBet}
      exitBanner={exitBanner}
      game={game}
      latestEventType={latestEventType}
      showBanner={showBanner}
    />
  )
}
